import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  History,
  RotateCcw,
  AlertTriangle,
  Loader2,
  Unlock,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  listUsers,
  approveUser,
  rejectUser,
  returnToPending,
  unblockUser,
  getUserAuditHistory,
  type User,
  type AuditHistoryEntry,
} from "@/services/usersService";
import { listClients, type Client } from "@/services/clientsService";
import { listProfiles, type Profile } from "@/services/profilesService";

interface PendingUser {
  id: string;
  email: string;
  name: string;
  provider: "google" | "email";
  requestedAt: Date;
  lastAccess?: Date;
  status: "pending" | "approved" | "rejected" | "blocked";
  status_name: string;
  status_id?: number; // Adicionar status_id para garantir detecção correta
  access_type_name: string;
  multipleAttempts?: boolean;
}

interface AccessValidationProps {
  onBack: () => void;
}

export const AccessValidation = ({ onBack }: AccessValidationProps) => {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [auditHistory, setAuditHistory] = useState<AuditHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    profile_id: "",
    client_ids: [] as string[],
  });
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Buscar usuários pendentes (status_id = 15), bloqueados (status_id = 16) e inativos (status_id = 14)
      // Baseado nos IDs reais do banco: active=13, inactive=14, pending=15, blocked=16
      // Incluindo inativos pois podem ter sido bloqueados mas com status diferente
      const [pendingResult, blockedResult, inactiveResult] = await Promise.all([
        listUsers({ limit: 10000, status_id: 15 }), // pending
        listUsers({ limit: 10000, status_id: 16 }), // blocked
        listUsers({ limit: 10000, status_id: 14 }), // inactive (pode ser bloqueado)
      ]);

      const allUsers: User[] = [];

      if (pendingResult.success && pendingResult.data) {
        allUsers.push(...pendingResult.data.users);
      } else if (pendingResult.error) {
        console.warn("Erro ao buscar usuários pendentes:", pendingResult.error);
      }

      if (blockedResult.success && blockedResult.data) {
        allUsers.push(...blockedResult.data.users);
      } else if (blockedResult.error) {
        console.warn(
          "Erro ao buscar usuários bloqueados:",
          blockedResult.error
        );
      }

      // Incluir inativos também (podem ter sido bloqueados)
      if (inactiveResult.success && inactiveResult.data) {
        allUsers.push(...inactiveResult.data.users);
      } else if (inactiveResult.error) {
        console.warn("Erro ao buscar usuários inativos:", inactiveResult.error);
      }

      // Remover duplicatas (caso algum usuário apareça em múltiplas buscas)
      const uniqueUsers = Array.from(
        new Map(allUsers.map((user) => [user.id, user])).values()
      );

      // Se ainda não encontrou nenhum, fazer busca alternativa sem filtro
      if (uniqueUsers.length === 0) {
        const allResult = await listUsers({ limit: 10000 });
        if (allResult.success && allResult.data) {
          const filtered = allResult.data.users.filter((user) => {
            const statusName = (user.status_name || "").toLowerCase();
            return (
              statusName === "pending" ||
              statusName === "blocked" ||
              statusName === "inactive"
            );
          });
          uniqueUsers.push(...filtered);
        }
      }

      // Log para debug
      console.log("Usuários encontrados:", {
        pending: pendingResult.data?.users?.length || 0,
        blocked: blockedResult.data?.users?.length || 0,
        inactive: inactiveResult.data?.users?.length || 0,
        total: uniqueUsers.length,
        users: uniqueUsers.map((u) => ({
          id: u.id,
          name: u.name,
          status: u.status_name,
          status_id: u.status_id,
        })),
      });

      // Log detalhado de todos os usuários recebidos
      console.log(
        "DEBUG - Todos os usuários recebidos:",
        uniqueUsers.map((u) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          status_name: u.status_name,
          status_id: u.status_id,
          status_name_lower: (u.status_name || "").toLowerCase(),
        }))
      );

      // Log detalhado de usuários bloqueados (por status_id ou status_name)
      const blockedUsers = uniqueUsers.filter(
        (u) =>
          u.status_id === 16 ||
          (u.status_name || "").toLowerCase().trim() === "blocked"
      );
      if (blockedUsers.length > 0) {
        console.log(
          "DEBUG - Usuários bloqueados encontrados:",
          blockedUsers.map((u) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            status_name: u.status_name,
            status_id: u.status_id,
            status_name_lower: (u.status_name || "").toLowerCase().trim(),
          }))
        );
      } else {
        console.log(
          "DEBUG - Nenhum usuário bloqueado encontrado por status_name. Verificando por status_id 16..."
        );
        const blockedById = uniqueUsers.filter((u) => u.status_id === 16);
        console.log(
          "DEBUG - Usuários com status_id 16:",
          blockedById.map((u) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            status_name: u.status_name,
            status_id: u.status_id,
          }))
        );
      }

      // Mapear usuários para o formato esperado
      const mappedUsers: PendingUser[] = uniqueUsers.map((user) => {
        const statusName = (user.status_name || "").toLowerCase().trim();
        const statusId = user.status_id;
        let status: "pending" | "approved" | "rejected" | "blocked" = "pending";

        // Priorizar status_id para garantir precisão
        if (statusId === 16) {
          // status_id 16 = blocked
          status = "blocked";
        } else if (statusId === 15) {
          // status_id 15 = pending
          status = "pending";
        } else if (statusId === 14) {
          // status_id 14 = inactive
          status = "rejected";
        } else if (statusId === 13) {
          // status_id 13 = active
          status = "approved";
        } else {
          // Fallback para status_name se status_id não for reconhecido
          if (statusName === "pending") {
            status = "pending";
          } else if (statusName === "blocked" || statusName.includes("block")) {
            status = "blocked";
          } else if (statusName === "inactive") {
            status = "rejected";
          } else if (statusName === "active" || statusName === "approved") {
            status = "approved";
          } else {
            status = "pending";
          }
        }

        const mappedUser: PendingUser = {
          id: user.id,
          email: user.email,
          name: user.name,
          provider: (user.access_type_name === "google"
            ? "google"
            : "email") as "google" | "email",
          requestedAt: new Date(user.created_at),
          status,
          status_name: user.status_name || "", // Preservar status_name da API exatamente como vem
          status_id: user.status_id, // Preservar status_id da API
          access_type_name: user.access_type_name,
        };

        return mappedUser;
      });

      console.log(
        "Usuários mapeados para exibição:",
        mappedUsers.map((u) => ({
          id: u.id,
          name: u.name,
          status: u.status,
          status_name: u.status_name,
        }))
      );

      setPendingUsers(mappedUsers);

      // Buscar clientes
      const clientsResult = await listClients();
      if (clientsResult.success && clientsResult.data) {
        setClients(clientsResult.data.clients || []);
      }

      // Buscar perfis
      const profilesResult = await listProfiles({ is_active: true });
      if (profilesResult.success && profilesResult.data) {
        setProfiles(profilesResult.data.data || []);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar os usuários pendentes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveClick = (user: PendingUser) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      profile_id: "",
      client_ids: [],
    });
    setIsApprovalDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedUser) return;

    try {
      const result = await approveUser(selectedUser.id, {
        profile_id: editForm.profile_id || undefined,
        client_ids: editForm.client_ids,
      });

      if (result.success) {
        toast({
          title: "Acesso aprovado",
          description: "O usuário foi aprovado e adicionado ao sistema.",
        });
        setIsApprovalDialogOpen(false);
        setSelectedUser(null);
        await loadData();
      } else {
        throw new Error(result.error?.message || "Erro ao aprovar usuário");
      }
    } catch (error) {
      toast({
        title: "Erro ao aprovar",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível aprovar o usuário.",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (userId: string) => {
    try {
      const result = await rejectUser(userId);

      if (result.success) {
        toast({
          title: "Acesso negado",
          description: "O acesso do usuário foi negado.",
          variant: "destructive",
        });
        await loadData();
      } else {
        throw new Error(result.error?.message || "Erro ao rejeitar usuário");
      }
    } catch (error) {
      toast({
        title: "Erro ao rejeitar",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível rejeitar o usuário.",
        variant: "destructive",
      });
    }
  };

  const handleBackToPending = async (userId: string) => {
    try {
      const result = await returnToPending(userId);

      if (result.success) {
        toast({
          title: "Status alterado",
          description: "Usuário movido de volta para pendente.",
        });
        await loadData();
      } else {
        throw new Error(result.error?.message || "Erro ao alterar status");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível alterar o status.",
        variant: "destructive",
      });
    }
  };

  const handleUnblock = async (userId: string) => {
    try {
      const result = await unblockUser(userId);

      if (result.success) {
        toast({
          title: "Usuário desbloqueado",
          description:
            "O usuário foi desbloqueado e pode fazer login novamente.",
        });
        await loadData();
      } else {
        throw new Error(result.error?.message || "Erro ao desbloquear usuário");
      }
    } catch (error) {
      toast({
        title: "Erro ao desbloquear",
        description:
          error instanceof Error
            ? error.message
            : "Não foi possível desbloquear o usuário.",
        variant: "destructive",
      });
    }
  };

  const showHistory = async (user: PendingUser) => {
    setSelectedUser(user);
    setIsLoadingHistory(true);
    setIsHistoryDialogOpen(true);

    try {
      const result = await getUserAuditHistory(user.id);
      if (result.success && result.data) {
        setAuditHistory(result.data);
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar histórico",
        description: "Não foi possível carregar o histórico de auditoria.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleClientToggle = (clientId: string) => {
    setEditForm((prev) => ({
      ...prev,
      client_ids: prev.client_ids.includes(clientId)
        ? prev.client_ids.filter((id) => id !== clientId)
        : [...prev.client_ids, clientId],
    }));
  };

  const getStatusBadge = (statusName: string) => {
    if (!statusName || statusName.trim() === "") {
      return (
        <Badge variant="secondary">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Desconhecido
        </Badge>
      );
    }

    const statusNameLower = statusName.toLowerCase().trim();


    const translations: Record<string, string> = {
      blocked: "Bloqueado",
      pending: "Pendente",
      active: "Ativo",
      approved: "Aprovado",
      rejected: "Rejeitado",
      inactive: "Inativo",
    };

    const translated = translations[statusNameLower] || statusName;

    // Determinar cor e ícone baseado no status (mantemos a lógica atual)
    let variant: "default" | "secondary" | "destructive" | "outline" =
      "secondary";
    let IconComponent = Clock;

    if (statusNameLower === "blocked") {
      variant = "destructive";
      IconComponent = XCircle;
    } else if (statusNameLower === "pending") {
      variant = "secondary";
      IconComponent = Clock;
    } else if (statusNameLower === "active" || statusNameLower === "approved") {
      variant = "default";
      IconComponent = CheckCircle;
    } else if (statusNameLower === "rejected") {
      variant = "destructive";
      IconComponent = XCircle;
    } else if (statusNameLower === "inactive") {
      variant = "secondary";
      IconComponent = XCircle;
    } else {
      // Qualquer outro status - usar secundário
      variant = "secondary";
      IconComponent = AlertTriangle;
    }

    // Capitalizar primeira letra do texto final
    const formattedText =
      translated.charAt(0).toUpperCase() + translated.slice(1);

    return (
      <Badge variant={variant}>
        <IconComponent className="h-3 w-3 mr-1" />
        {formattedText}
      </Badge>
    );
  };

  const getProviderBadge = (provider: PendingUser["provider"]) => {
    return (
      <Badge variant="outline">
        {provider === "google" ? "Google" : "Email"}
      </Badge>
    );
  };

  const formatAuditAction = (action: string, actionType: string) => {
    if (action.toLowerCase().includes("approve") || actionType === "CREATE") {
      return { label: "Aprovado", icon: CheckCircle, color: "text-green-600" };
    }
    if (action.toLowerCase().includes("reject") || actionType === "DELETE") {
      return { label: "Rejeitado", icon: XCircle, color: "text-red-600" };
    }
    if (action.toLowerCase().includes("pending") || actionType === "UPDATE") {
      return {
        label: "Status Alterado",
        icon: RotateCcw,
        color: "text-blue-600",
      };
    }
    return { label: action, icon: AlertTriangle, color: "text-amber-500" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={User}
        title="Validação de Acesso"
        description="Gerencie solicitações de acesso à plataforma"
        onBack={onBack}
      />
      <div className="container mx-auto px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>Usuários Pendentes</span>
            </CardTitle>
            <CardDescription>
              Usuários que solicitaram acesso e aguardam aprovação
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Provedor</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        Nenhum usuário pendente encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingUsers.map((user) => {
                      // Debug log para cada usuário renderizado
                      if (
                        user.status === "blocked" ||
                        user.status_name?.toLowerCase().includes("block")
                      ) {
                        console.log(
                          "DEBUG Render - Usuário bloqueado sendo renderizado:",
                          {
                            id: user.id,
                            name: user.name,
                            status: user.status,
                            status_name: user.status_name,
                          }
                        );
                      }

                      return (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center space-x-2">
                              <span>{user.name}</span>
                              {user.multipleAttempts && (
                                <div className="relative group">
                                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                    Múltiplas tentativas de acesso
                                  </div>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            {getProviderBadge(user.provider)}
                          </TableCell>
                          <TableCell>
                            {user.requestedAt.toLocaleDateString("pt-BR")} às{" "}
                            {user.requestedAt.toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(user.status_name || "")}
                          </TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              {user.status === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveClick(user)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Aprovar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleReject(user.id)}
                                  >
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Rejeitar
                                  </Button>
                                </>
                              )}
                              {user.status === "blocked" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleUnblock(user.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Unlock className="h-3 w-3 mr-1" />
                                    Desbloquear
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleBackToPending(user.id)}
                                  >
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    Voltar para Pendente
                                  </Button>
                                </>
                              )}
                              {user.status === "rejected" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleBackToPending(user.id)}
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Voltar para Pendente
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => showHistory(user)}
                              >
                                <History className="h-3 w-3 mr-1" />
                                Histórico
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Approval Dialog */}
        <Dialog
          open={isApprovalDialogOpen}
          onOpenChange={setIsApprovalDialogOpen}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Aprovar Usuário</DialogTitle>
              <DialogDescription>
                Complete as informações para aprovar o acesso do usuário
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={editForm.email}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="profile">Perfil</Label>
                <Select
                  value={editForm.profile_id}
                  onValueChange={(value) =>
                    setEditForm((prev) => ({ ...prev, profile_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tipo de Acesso</Label>
                <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted">
                  <span className="text-sm">
                    {selectedUser?.access_type_name || "N/A"}
                  </span>
                  <Badge variant="outline">
                    {selectedUser?.provider === "google" ? "Google" : "Email"}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Clientes</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {clients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={client.id}
                        checked={editForm.client_ids.includes(client.id)}
                        onCheckedChange={() => handleClientToggle(client.id)}
                      />
                      <Label
                        htmlFor={client.id}
                        className="text-sm cursor-pointer"
                      >
                        {client.name} - {client.company}
                      </Label>
                    </div>
                  ))}
                  {clients.length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-2">
                      Nenhum cliente disponível
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsApprovalDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApprove}
                className="bg-green-600 hover:bg-green-700"
                disabled={!editForm.name || editForm.client_ids.length === 0}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Aprovar e Salvar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog
          open={isHistoryDialogOpen}
          onOpenChange={setIsHistoryDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Histórico de Ações</DialogTitle>
              <DialogDescription>
                Histórico de aprovações e rejeições para {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : auditHistory.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  Nenhum histórico disponível
                </p>
              ) : (
                auditHistory.map((entry) => {
                  const {
                    label,
                    icon: Icon,
                    color,
                  } = formatAuditAction(entry.action, entry.action_type);
                  return (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className={`h-4 w-4 ${color}`} />
                        <span>{label}</span>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <div>
                          {new Date(entry.date).toLocaleDateString("pt-BR")} às{" "}
                          {new Date(entry.date).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        {entry.action_user && (
                          <div>por {entry.action_user}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
